import { useEffect, useState } from 'react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { userHasPermission } from '@/shared/lib/roleUtils'
import { RH } from '@/constants/permissions'
import {
  vistaDeDireccion,
  type CandidatoParaDireccion,
  type ModalidadDeEntrevista,
} from '@/features/rh/api/vistaDeDireccion'
import {
  fechaHoraYaPaso,
  hoyEnLaOperacion,
  mensajeDeFallo,
} from '@/features/rh/lib/vistaDeDireccion'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface Entrevistador {
  id: string
  nombre: string
}

interface Props {
  candidato: CandidatoParaDireccion
  /** Se inyecta para no atar este modal al cliente de usuarios. */
  cargarUsuarios: () => Promise<unknown[]>
  onCancelar: () => void
  onAgendada: () => void
}

/**
 * Agendar una entrevista de Dirección (SRH-283).
 *
 * No reutiliza `ScheduleHiringManagerInterview` tal cual: ese formulario trae
 * el flujo del solicitante encima —elige candidato de su propia lista y saca
 * los entrevistadores de un endpoint del jefe—, y aquí el candidato ya está
 * dado y los elegibles son otros: quienes pueden decidir por Dirección.
 */
export const AgendarEntrevistaDeDireccion = ({
  candidato,
  cargarUsuarios,
  onCancelar,
  onAgendada,
}: Props) => {
  const [entrevistadores, setEntrevistadores] = useState<Entrevistador[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  const [fecha, setFecha] = useState('')
  const [hora, setHora] = useState('')
  const [modalidad, setModalidad] = useState<ModalidadDeEntrevista>('presencial')
  const [lugar, setLugar] = useState('')
  const [entrevistador, setEntrevistador] = useState('')

  useEffect(() => {
    let vigente = true
    cargarUsuarios()
      .then((usuarios) => {
        if (!vigente) return
        // El back solo valida que el usuario exista; el recorte a quien puede
        // decidir por Dirección es nuestro, para no ofrecer a toda la empresa.
        const elegibles = (usuarios as Array<Record<string, unknown>>)
          .filter((u) => u?.isActive !== false)
          .filter((u) =>
            userHasPermission(
              RH.RECLUTAMIENTO_DECIDIR_APROBADOS,
              (u?.rbac as { effectivePermissions?: string[] })?.effectivePermissions ?? [],
            ),
          )
          .map((u) => ({
            id: String(u.id),
            nombre:
              `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
              String(u.email ?? 'Sin nombre'),
          }))
        setEntrevistadores(elegibles)
      })
      .catch(() => vigente && setEntrevistadores([]))
      .finally(() => vigente && setCargando(false))
    return () => {
      vigente = false
    }
  }, [cargarUsuarios])

  // No se agenda en el pasado (SRH-312). El servidor lo valida igual: esto evita
  // el viaje y dice el porque junto a los campos.
  const yaPaso = fechaHoraYaPaso(fecha, hora)
  const falta = !fecha || !hora || !entrevistador || yaPaso

  const guardar = async () => {
    setGuardando(true)
    setError(null)
    try {
      await vistaDeDireccion.agendar({
        candidateId: candidato.candidateId,
        interviewDate: fecha,
        interviewTime: hora,
        modality: modalidad,
        locationOrLink: lugar.trim() || undefined,
        interviewerUserId: entrevistador,
      })
      onAgendada()
    } catch (errorCrudo: unknown) {
      setError(mensajeDeFallo(errorCrudo))
    } finally {
      setGuardando(false)
    }
  }

  const campo =
    'w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA]'
  const etiqueta = 'block text-xs font-medium text-[#777777] mb-1.5'

  return (
    <CapaDeModal onClickFondo={guardando ? undefined : onCancelar}>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-base font-semibold text-[#1E1E1F]">
          Agendar entrevista de Dirección
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Agendar no mueve al candidato: sigue en validación hasta que se
          registre el resultado. Se pueden agendar varias.
        </p>
        <p className="text-sm text-gray-700 mt-3">
          Candidato: <span className="font-medium">{candidato.fullName}</span>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label htmlFor="fecha-direccion" className={etiqueta}>
              Fecha <span className="text-red-500">*</span>
            </label>
            <input
              id="fecha-direccion"
              type="date"
              value={fecha}
              min={hoyEnLaOperacion()}
              onChange={(e) => setFecha(e.target.value)}
              className={`date-brand ${campo}`}
              style={{ colorScheme: 'light' }}
            />
          </div>
          <div>
            <label htmlFor="hora-direccion" className={etiqueta}>
              Hora <span className="text-red-500">*</span>
            </label>
            <input
              id="hora-direccion"
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              className={campo}
              style={{ colorScheme: 'light' }}
            />
          </div>
        </div>

        {yaPaso && (
          <p role="alert" className="mt-2 text-xs text-red-600">
            La fecha y la hora ya pasaron. Elige una futura.
          </p>
        )}

        <div className="mt-4">
          <label htmlFor="modalidad-direccion" className={etiqueta}>
            Modalidad <span className="text-red-500">*</span>
          </label>
          <SelectBuscable
            variante="formulario"
            id="modalidad-direccion"
            value={modalidad}
            onChange={(valor) => setModalidad(valor as ModalidadDeEntrevista)}
            canDelete={false}
            placeholderDeBusqueda="Buscar modalidad..."
            opciones={[
              { value: 'presencial', label: 'Presencial' },
              { value: 'virtual', label: 'Virtual' },
            ]}
          />
        </div>

        <div className="mt-4">
          <label htmlFor="lugar-direccion" className={etiqueta}>
            {modalidad === 'virtual' ? 'Enlace' : 'Lugar'}{' '}
            <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <input
            id="lugar-direccion"
            type="text"
            value={lugar}
            onChange={(e) => setLugar(e.target.value)}
            placeholder={modalidad === 'virtual' ? 'https://…' : 'Sala 2'}
            className={campo}
          />
        </div>

        <div className="mt-4">
          <label htmlFor="entrevistador-direccion" className={etiqueta}>
            Entrevistador <span className="text-red-500">*</span>
          </label>
          <SelectBuscable
            variante="formulario"
            id="entrevistador-direccion"
            value={entrevistador}
            onChange={setEntrevistador}
            disabled={cargando}
            placeholder={cargando ? 'Cargando…' : 'Selecciona quién entrevista'}
            placeholderDeBusqueda="Buscar entrevistador..."
            opciones={entrevistadores.map((e) => ({
              value: String(e.id),
              label: e.nombre,
            }))}
          />
          {!cargando && entrevistadores.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Nadie tiene el permiso de decidir por Dirección. Hay que
              asignarlo en Configuración → Roles.
            </p>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={guardando || falta}
            className={estiloDeBoton('primario')}
          >
            {guardando ? 'Agendando…' : 'Agendar'}
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}
